import { useEffect, useRef, useState } from 'react';
import stylex from '@stylexjs/stylex';
import { Modal, Button, TextField, PreloaderCircular } from '@shared/ui/kit';
import { EmailSubjectField } from '@pages/event-send-invitations/ui/shared/email-subject-field';
import { Select, SelectValue } from '@shared/ui/select';
import { CustomDropdown } from '@shared/ui/dropdown';
import { XmarkIcon } from '@shared/assets/icons';
import { PUBLISHED_FORM_STATUS } from '@entities/leadCollector';
import { useFetchForms } from '@entities/leadCollector/lib/hooks/useFetchForms';
import {
  TICKET_CHANNEL_SELECT_OPTIONS,
  mapSelectedChannelsToSendChannels,
} from '@pages/event-send-invitations/lib/invitation-channel-options';
import { useToast } from '@shared/hooks/useToast';
import { useSendRegistrationLinks } from '../../api/useSendRegistrationLinks';
import { useSendMessagesFlow } from '@pages/event-send-invitations/lib/use-send-messages-flow';
import { SendContentType } from '@pages/event/api/useSendMessages';
import { SendStatisticsModal } from '@pages/event-send-invitations/ui/send-statistics-modal';
import { styles, filterStyles } from './styles';

export const RegistrationLinksModal = ({
  active,
  onClose,
  count,
  eventId,
  formId: eventFormId,
  ticketIds,
  onSent,
  onStarted,
  onSendingChange,
}: {
  active: boolean;
  onClose: () => void;
  count: number;
  eventId: number;
  formId: number;
  ticketIds: number[];
  onSent?: () => void;
  onStarted?: () => void;
  onSendingChange?: (sending: boolean) => void;
}) => {
  const { forms, fetching: loadingForms, error: formsError } = useFetchForms(!active);
  const [formId, setFormId] = useState<number | null>(eventFormId);
  const [channels, setChannels] = useState<SelectValue[]>([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const { showToast } = useToast();
  const options = (forms ?? [])
    .filter((form) => form.status === PUBLISHED_FORM_STATUS)
    .map((form) => ({ id: form.id, title: form.name }));
  const selectedForm = options.find((form) => form.id === formId) ?? null;
  const canSend = !!selectedForm && !loadingForms && !formsError && channels.length > 0 && !!message.trim();
  const [, send] = useSendRegistrationLinks();
  const flow = useSendMessagesFlow({
    eventId,
    contentType: SendContentType.Registration,
    onComplete: onClose,
    trackInBackground: true,
    onTrackingTimeout: () => {
      showToast(
        'Отправка на сервере продолжается. Автоматическое ожидание статистики завершено; обновите страницу позже, чтобы проверить статусы.',
        'info'
      );
      onSent?.();
      onClose();
    },
  });
  const deliveredAttempt = useRef<number | null>(null);
  useEffect(() => {
    onSendingChange?.(flow.isWaitingForSend || flow.isStartedFlowActive);
  }, [flow.isWaitingForSend, flow.isStartedFlowActive, onSendingChange]);
  useEffect(() => {
    if (
      flow.messageAttempt !== null &&
      flow.messagesStats?.isSent &&
      flow.messageAttempt !== deliveredAttempt.current
    ) {
      deliveredAttempt.current = flow.messageAttempt;
      onSent?.();
    }
  }, [flow.messagesStats?.isSent, flow.messageAttempt, onSent]);
  useEffect(() => {
    if (!active && flow.isStatisticsModalVisible) flow.closeStatistics();
  }, [active, flow.isStatisticsModalVisible, flow.closeStatistics]);
  const handleSend = () => {
    if (!canSend || !selectedForm) return;
    void flow.runSend(async () => {
      const result = await send({
        input: {
          eventId,
          formId: selectedForm.id,
          ticketIds,
          channels: mapSelectedChannelsToSendChannels(channels),
          message: message.trim(),
          emailSubject: subject.trim() || null,
          emailMessage: null,
        },
      });
      if (result.error) {
        showToast(result.error.message, 'error');
        return undefined;
      }
      const response = result.data?.sendRegistrationLinks;
      if (!response) {
        showToast('Не удалось начать отправку ссылок. Попробуйте ещё раз.', 'error');
        return undefined;
      }
      if (!response.queued) {
        showToast(
          `Нет доступных получателей. Уже зарегистрированы или заявка принята: ${response.alreadyRegistered}. Недоступны для выбранных каналов: ${response.unavailable}.`,
          'info'
        );
        onSent?.();
        return undefined;
      }
      onStarted?.();
      return response.attempt;
    });
  };
  if (flow.isWaitingForSend || flow.isStartedFlowActive)
    return (
      <Modal
        active={active}
        settingsContainer={{ height: 'auto', padding: '0px', overflow: 'visible' }}
        settingsHeader={{ padding: '0px' }}
        settingsFooter={{ padding: '0px' }}
        settings={{ overflow: 'visible' }}
      >
        <div {...stylex.props(styles.container)}>
          <div {...stylex.props(styles.headerContainer)}>
            <p {...stylex.props(styles.header)}>Отправка ссылок на регистрацию</p>
            <XmarkIcon size="24" onClick={onClose} />
          </div>
          <div {...stylex.props(styles.loader)}>
            <PreloaderCircular variant="secondary" size="large" />
            <p {...stylex.props(styles.text)}>
              Мы отправляем ссылки на регистрацию.
              <br />
              Процесс может занять некоторое время
            </p>
          </div>
          <div {...stylex.props(styles.btn)}>
            <Button size="small" variant="secondary" onClick={onClose}>
              Закрыть
            </Button>
          </div>
        </div>
      </Modal>
    );
  if (flow.isStatisticsModalVisible)
    return (
      <SendStatisticsModal
        active={active}
        messagesStats={flow.messagesStats}
        contentType={SendContentType.Registration}
        onClose={flow.closeStatistics}
      />
    );
  return (
    <Modal
      active={active}
      settingsContainer={{ height: 'auto', padding: '0px', overflow: 'visible' }}
      settingsHeader={{ padding: '0px' }}
      settingsFooter={{ padding: '0px' }}
      settings={{ overflow: 'visible' }}
    >
      <div {...stylex.props(styles.container)}>
        <div {...stylex.props(styles.headerContainer)}>
          <p {...stylex.props(styles.header)}>Отправить ссылку на регистрацию</p>
          <XmarkIcon size="24" onClick={onClose} />
        </div>
        <div {...stylex.props(filterStyles.sendInvite)}>
          <p {...stylex.props(filterStyles.sendInviteText)}>Выбрано участников: {count}</p>
          <CustomDropdown
            labelMessage="Шаблон регистрации"
            placeholder="Выберите шаблон"
            options={options}
            itemText="title"
            itemValue="id"
            value={selectedForm}
            changeFunc={(form) => setFormId(form?.id ?? null)}
          />
          <Select
            label="Тип отправки"
            placeholder="Выбрать тип"
            options={TICKET_CHANNEL_SELECT_OPTIONS}
            selectedValue={channels}
            onChange={(values) => setChannels(Array.isArray(values) ? values : [values])}
            isWithChips
          />
          {channels.includes('email') && (
            <EmailSubjectField label="Тема письма" subject={subject} onSubjectChange={setSubject} />
          )}
          <div {...stylex.props(messageStyles.container)}>
            <TextField
              labelMsg="Текст сообщения"
              value={message}
              changeFunction={(event) => setMessage(event.target.value)}
              settings={messageStyles.textarea}
            />
          </div>
          <p {...stylex.props(styles.smsBlockLabel)}>Персональная ссылка добавится автоматически при отправке.</p>
        </div>
        <div {...stylex.props(filterStyles.actionButton)}>
          <Button size="small" variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button size="small" variant="accent" isDisabled={!canSend} onClick={handleSend}>
            Отправить ссылки
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const messageStyles = stylex.create({
  container: {
    width: '100%',
  },
  textarea: {
    width: '100%',
    minHeight: '102px',
  },
});
